import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { buildBusinessProfileSummary } from '../../../model/helpers';
import type { OnboardingBusinessProfile } from '../../../model/types';
import FigmaStepModal from '../../modal/FigmaStepModal';
import { useGoServerStore } from '@/stores/goServerStore';
import { ONBOARDING_STEP_LABELS, ONBOARDING_TOTAL_STEPS } from '../../../model/constants';

interface BusinessInfoDetailsStepProps {
  businessProfile: OnboardingBusinessProfile;
  savedDescription?: string;
  isSaving: boolean;
  error?: string | null;
  onComplete: (description: string) => Promise<void>;
  onBack: () => void;
}

type Phase = 'generating' | 'streaming' | 'done' | 'error';

const PROCESSING_LABELS = [
  '...סורק פרופיל עסקי',
  '...מזהה יתרונות תחרותיים',
  '...מסנתז תיאור',
  '...מסכם',
] as const;

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
const FINAL_STEP_NUMBER = 3;

const ensureSentence = (label: string, value: string): string | null => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  const withPunctuation = /[.!?…]$/.test(normalizedValue)
    ? normalizedValue
    : `${normalizedValue}.`;

  return `${label} ${withPunctuation}`;
};

const buildFallbackBusinessDescription = (businessProfile: OnboardingBusinessProfile): string => {
  const sections = [
    ensureSentence('העסק מתמחה ב', businessProfile.whatYouDo),
    ensureSentence('לקוחות בוחרים בכם כי', businessProfile.whyChooseYou),
    ensureSentence('בלב ההצעה שלכם נמצאים', businessProfile.productDetails),
    ensureSentence('הלידים מגיעים בעיקר דרך', businessProfile.leadSourcesAndGoal),
    ensureSentence('בנוסף, חשוב לצוות לדעת ש', businessProfile.commonQuestionsAndAnswers),
  ].filter(Boolean);

  if (sections.length === 0) {
    return '';
  }

  return sections.join('\n\n');
};

const extractAgentErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'שגיאה בניתוח — נסה שוב';
};

type GeneratedInfoCard = {
  title: string;
  content: string;
};

const buildTextFromCards = (cards: GeneratedInfoCard[]): string =>
  cards
    .map((card) => (card.title ? `**${card.title}**\n${card.content}` : card.content))
    .join('\n\n');

const parseGeneratedInfoCards = (text: string): GeneratedInfoCard[] => {
  const normalized = text.trim();
  if (!normalized) {
    return [];
  }

  // Only treat **...** as a section title when it appears at the start of a line.
  // Inline bold (e.g. "מוצר **מודגש**") must not split card content.
  const markerRegex = /^\*\*(.+?)\*\*\s*/gm;
  const matches = [...normalized.matchAll(markerRegex)];

  if (matches.length === 0) {
    return [{ title: '', content: normalized }];
  }

  const cards: GeneratedInfoCard[] = [];

  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const nextMatch = matches[i + 1];

    const title = currentMatch[1].trim();
    const contentStart = (currentMatch.index ?? 0) + currentMatch[0].length;
    const contentEnd = nextMatch?.index ?? normalized.length;
    const content = normalized.slice(contentStart, contentEnd).trim();

    if (!title && !content) {
      continue;
    }

    cards.push({
      title,
      content,
    });
  }

  return cards.length > 0 ? cards : [{ title: '', content: normalized }];
};

const MAX_DESCRIPTION_CHARS = 2500;

const BusinessInfoDetailsStep: React.FC<BusinessInfoDetailsStepProps> = ({
  businessProfile,
  savedDescription,
  isSaving,
  error,
  onComplete,
  onBack,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('he') || i18n.language.startsWith('ar');
  const { sdk, initializeSDK } = useGoServerStore();

  const [phase, setPhase] = useState<Phase>('generating');
  const [activeLabelIndex, setActiveLabelIndex] = useState(0);
  const [completedLabelIndices, setCompletedLabelIndices] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState(0);
  const [fullText, setFullText] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const [editedText, setEditedText] = useState('');
  const [editedCards, setEditedCards] = useState<GeneratedInfoCard[]>([]);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  const labelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const typingStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultContainerRef = useRef<HTMLDivElement | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);

  const clearAllTimers = () => {
    if (labelTimerRef.current !== null) { clearTimeout(labelTimerRef.current); labelTimerRef.current = null; }
    if (animationFrameRef.current !== null) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    if (typingStartTimerRef.current !== null) { clearTimeout(typingStartTimerRef.current); typingStartTimerRef.current = null; }
  };

  const startLabelSequencer = () => {
    let idx = 0;
    const totalLabels = PROCESSING_LABELS.length;
    const advance = () => {
      if (idx >= totalLabels - 1) return;
      setCompletedLabelIndices((prev) => new Set([...prev, idx]));
      idx++;
      setActiveLabelIndex(idx);
      setProgress(Math.round((idx / (totalLabels - 1)) * 75));
      labelTimerRef.current = setTimeout(advance, 1700);
    };
    labelTimerRef.current = setTimeout(advance, 1700);
  };

  const finishWithGeneratedText = (text: string, mode: 'remote' | 'fallback') => {
    clearAllTimers();
    setCompletedLabelIndices(new Set([0, 1, 2, 3]));
    setActiveLabelIndex(PROCESSING_LABELS.length - 1);
    setProgress(100);

    const normalizedText = text.trim();
    setFullText(normalizedText);
    setEditedText(normalizedText);
    setEditedCards(parseGeneratedInfoCards(normalizedText));

    if (mode === 'fallback') {
      setDisplayedText(normalizedText);
      setPhase('done');
      return;
    }

    setDisplayedText('');
    setPhase('streaming');
  };

  useEffect(() => {
    const cachedDescription = savedDescription?.trim() ?? '';
    if (cachedDescription) {
      clearAllTimers();
      setAgentError(null);
      setFallbackNotice(null);
      finishWithGeneratedText(cachedDescription, 'fallback');
      return;
    }

    if (!sdk) {
      initializeSDK();
      return;
    }

    setPhase('generating');
    setActiveLabelIndex(0);
    setCompletedLabelIndices(new Set());
    setProgress(0);
    setFullText('');
    setDisplayedText('');
    setEditedText('');
    setEditedCards([]);
    setAgentError(null);
    setFallbackNotice(null);

    startLabelSequencer();

    const businessInfo = buildBusinessProfileSummary(businessProfile);
    const fallbackText = buildFallbackBusinessDescription(businessProfile);

    sdk.sales
      .runTourAgent({
        agentName: 'OnboardingBusinessInfoDetails',
        questionText: '',
        businessInfo,
      })
      .then((result) => {
        const text = String(result.result ?? '').trim();
        if (!text && fallbackText) {
          setFallbackNotice('לא הצלחנו לקבל ניסוח מהשרת המקומי, אז יצרנו עבורך טיוטה ראשונית שאפשר לערוך.');
          finishWithGeneratedText(fallbackText, 'fallback');
          return;
        }

        if (!text) {
          clearAllTimers();
          setAgentError('לא התקבל תיאור אוטומטי — נסה שוב');
          setPhase('error');
          return;
        }

        finishWithGeneratedText(text, 'remote');
      })
      .catch((err: Error & { status?: number }) => {
        clearAllTimers();

        if (fallbackText) {
          const isMissingTourEndpoint = err?.status === 404;
          setFallbackNotice(
            isMissingTourEndpoint
              ? 'השרת המקומי עדיין לא תומך ביצירת התיאור האוטומטי, אז יצרנו עבורך טיוטה ראשונית שאפשר לערוך ולשמור.'
              : 'לא הצלחנו לקבל ניסוח מהשרת המקומי, אז יצרנו עבורך טיוטה ראשונית שאפשר לערוך ולשמור.'
          );
          finishWithGeneratedText(fallbackText, 'fallback');
          return;
        }

        setAgentError(extractAgentErrorMessage(err));
        setPhase('error');
      });

    return clearAllTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessProfile, initializeSDK, sdk, savedDescription]);


  useEffect(() => {
    if (phase !== 'streaming' || !fullText) return;

    const CHARS_PER_SECOND = 80;
    let startTime: number | null = null;

    const frame = (ts: number) => {
      if (startTime === null) startTime = ts;
      const shown = Math.min(
        Math.floor(((ts - startTime) / 1000) * CHARS_PER_SECOND),
        fullText.length,
      );
      setDisplayedText(fullText.slice(0, shown));
      if (shown < fullText.length) {
        animationFrameRef.current = requestAnimationFrame(frame);
      } else {
        animationFrameRef.current = null;
        setPhase('done');
      }
    };

    // Small delay so typing starts after the result-phase fade-in (0.28s) settles.
    typingStartTimerRef.current = setTimeout(() => {
      typingStartTimerRef.current = null;
      animationFrameRef.current = requestAnimationFrame(frame);
    }, 320);

    return () => {
      if (typingStartTimerRef.current !== null) {
        clearTimeout(typingStartTimerRef.current);
        typingStartTimerRef.current = null;
      }
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [phase, fullText]);

  const handleSave = async () => {
    const raw = (editedText.trim() || fullText).slice(0, MAX_DESCRIPTION_CHARS);
    await onComplete(raw);
  };

  const autosizeTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const handleCardChange = (idx: number, field: 'title' | 'content', value: string) => {
    setEditedCards((prev) => {
      const updated = prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
      setEditedText(buildTextFromCards(updated));
      return updated;
    });
  };

  const isGenerating = phase === 'generating';
  const isStreaming = phase === 'streaming';
  const isDone = phase === 'done';
  const isError = phase === 'error';
  const showResult = isStreaming || isDone || isError;
  const textAreaValue = isStreaming ? displayedText : editedText;
  const infoCards = parseGeneratedInfoCards(textAreaValue);

  useEffect(() => {
    if (!showResult) return;
    resultContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showResult]);

  useEffect(() => {
    if (!isStreaming || !cardsContainerRef.current) return;
    cardsContainerRef.current.scrollTop = cardsContainerRef.current.scrollHeight;
  }, [isStreaming, displayedText]);

  return (
    <FigmaStepModal
      embedded={true}
      stepNumber={FINAL_STEP_NUMBER}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      stepLabels={[...ONBOARDING_STEP_LABELS]}
      titleBelow
      title="פרופיל עסק שלי"
      description="זה סיכום פעילות העסק שלכם לבינה המלאכותית כדי שהיא תכיר אתכם באופן אישי, יש לוודא שכל הפרטים מדויקים ונכונים (ניתן לערוך כעת ובעתיד)."
      error={error}
      showBadge={false}
      footerPrimary={
        <AnimatePresence>
          {(isDone || isError) && (
            <motion.button
              key="save-btn"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.22, ease: EASE_OUT_EXPO }}
              onClick={() => void handleSave()}
              disabled={isSaving || isError || (isDone && editedText.trim().length === 0)}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-500 dark:hover:bg-brand-400"
            >
              {isSaving ? 'שומר...' : 'מאשר, תראו לי את המערכת מבפנים'}
            </motion.button>
          )}
        </AnimatePresence>
      }
      footerSecondary={
        <AnimatePresence>
          {(isDone || isError) && (
            <motion.button
              key="back-btn"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, delay: 0.08 }}
              onClick={onBack}
              className="rounded-full px-6 py-[14px] text-base font-bold text-text-800 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
            >
              הקודם
            </motion.button>
          )}
        </AnimatePresence>
      }
    >
      <div className="relative min-h-[220px] w-full" dir={isRTL ? 'rtl' : 'ltr'}>
        <AnimatePresence mode="wait">
          {isGenerating && (
            <motion.div
              key="generating-phase"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-5 py-4 just"
            >
              <div className="flex flex-col gap-3" dir={!isRTL ? 'rtl' : 'ltr'}>
                {PROCESSING_LABELS.map((label, i) => {
                  const isCompleted = completedLabelIndices.has(i);
                  const isActive = i === activeLabelIndex && !isCompleted;
                  const isPending = i > activeLabelIndex;

                  return (
                    <motion.div
                      key={label}
                      initial={{ opacity: 0, x: isRTL ? 8 : -8 }}
                      animate={{ opacity: isPending ? 0.35 : 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.06, ease: EASE_OUT_EXPO }}
                      className="flex items-center gap-3"
                    >
                      <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                        {isCompleted ? (
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.22, ease: EASE_OUT_EXPO }}
                          >
                            <CheckCircleIcon className="h-5 w-5 text-brand-500 dark:text-brand-400" />
                          </motion.div>
                        ) : isActive ? (
                          <motion.div
                            className="h-2.5 w-2.5 rounded-full bg-brand-500"
                            animate={{ opacity: [1, 0.25, 1] }}
                            transition={{ repeat: Infinity, duration: 0.9, ease: 'easeInOut' }}
                          />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                        )}
                      </div>

                      <span
                        className={`text-sm font-medium transition-colors ${
                          isCompleted
                            ? 'text-neutral-500 dark:text-neutral-400'
                            : isActive
                              ? 'text-neutral-900 dark:text-neutral-100'
                              : 'text-neutral-400 dark:text-neutral-600'
                        }`}
                      >
                        {label}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              <div className="h-1 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-brand-700 to-brand-400"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                />
              </div>
            </motion.div>
          )}

          {showResult && (
            <motion.div
              key="result-phase"
              ref={resultContainerRef}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: EASE_OUT_EXPO }}
              className="flex flex-col gap-3 py-4"
            >
              {isError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{agentError}</p>
              ) : (
                <>
                  {fallbackNotice ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                      {fallbackNotice}
                    </div>
                  ) : null}
                  <div
                    ref={cardsContainerRef}
                    className="max-h-[380px] space-y-3 overflow-y-auto p-1"
                  >
                    {isStreaming
                      ? infoCards.map((card, idx) => (
                          <div
                            key={`stream-${card.title}-${idx}`}
                            className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800"
                          >
                            {card.title ? (
                              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                {card.title}
                              </h4>
                            ) : null}
                            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
                              {card.content || '—'}
                            </p>
                          </div>
                        ))
                      : editedCards.map((card, idx) => (
                          <div
                            key={`edit-${idx}`}
                            className="rounded-lg border border-neutral-200 bg-white p-4 transition-colors hover:border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600"
                          >
                            {card.title ? (
                              <input
                                type="text"
                                value={card.title}
                                onChange={(e) => handleCardChange(idx, 'title', e.target.value)}
                                className="w-full rounded-md border border-neutral-200 bg-transparent px-2 py-1 text-sm font-semibold text-neutral-900 outline-none transition-colors focus:border-brand-400 dark:border-neutral-600 dark:text-neutral-100 dark:focus:border-brand-500"
                              />
                            ) : null}
                            <textarea
                              value={card.content}
                              onChange={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                                handleCardChange(idx, 'content', e.target.value);
                              }}
                              ref={autosizeTextareaRef}
                              className="mt-2 w-full resize-none overflow-hidden rounded-md border border-neutral-200 bg-transparent px-2 py-1 text-sm leading-relaxed text-neutral-700 outline-none transition-colors focus:border-brand-400 dark:border-neutral-600 dark:text-neutral-300 dark:focus:border-brand-500"
                            />
                          </div>
                        ))}
                  </div>

                  <AnimatePresence>
                    {isDone && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-between"
                      >
                        {textAreaValue.trim().length === 0 ? (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            תיאור ריק לא יישמר — הזן לפחות משפט אחד
                          </p>
                        ) : textAreaValue.length > MAX_DESCRIPTION_CHARS ? (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            הטקסט יקוצר ל-{MAX_DESCRIPTION_CHARS} תווים בעת השמירה
                          </p>
                        ) : null}
                        <p className={`ms-auto text-xs ${textAreaValue.length > MAX_DESCRIPTION_CHARS ? 'text-red-500 font-medium' : 'text-neutral-400 dark:text-neutral-500'}`}>
                          {textAreaValue.length} / {MAX_DESCRIPTION_CHARS}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FigmaStepModal>
  );
};

export default BusinessInfoDetailsStep;
